<template>
  <div :class="b()">
    <div :class="b('container')">
      <!-- Left Panel: Pages List -->
      <div :class="b('sidebar')">
        <div :class="b('sidebar-header')">
          <h3>Pages</h3>
          <a-button
            type="primary"
            size="small"
            @click="addPage"
          >
            + Add Page
          </a-button>
        </div>

        <div :class="b('pages-list')">
          <div
            v-for="(page, index) in pages"
            :key="index"
            :class="b('page-item', { active: currentPageIndex === index })"
            role="button"
            tabindex="0"
            @click="currentPageIndex = index"
            @keydown.enter="currentPageIndex = index"
            @keydown.space.prevent="currentPageIndex = index"
          >
            <span>Page {{ index + 1 }}</span>
            <a-button
              v-if="pages.length > 1"
              type="text"
              size="small"
              danger
              @click.stop="removePage(index)"
            >
              ×
            </a-button>
          </div>
        </div>
      </div>

      <!-- Center: Canvas -->
      <div :class="b('canvas-area')">
        <div :class="b('canvas-toolbar')">
          <a-space>
            <span>Page {{ currentPageIndex + 1 }} of {{ pages.length }}</span>
            <a-button size="small" @click="addSlot">+ Add Slot</a-button>
          </a-space>
        </div>

        <div
          ref="canvasContainer"
          :class="b('canvas')"
          :style="{
            width: canvasWidth + 'px',
            height: canvasHeight + 'px',
            backgroundColor: currentPage.backgroundColor || '#ffffff',
          }"
          role="button"
          tabindex="0"
          @click="handleCanvasClick"
          @keydown.enter="handleCanvasClick"
        >
          <!-- Render slots -->
          <div
            v-for="(slot, index) in currentPage.slots"
            :key="slot.slotId"
            :class="b('slot', { selected: selectedSlotIndex === index })"
            :style="getSlotStyle(slot)"
            role="button"
            tabindex="0"
            @click.stop="selectSlot(index)"
            @keydown.enter="selectSlot(index)"
            @mousedown="startDrag(index, $event)"
          >
            <div :class="b('slot-label')">Slot {{ index + 1 }}</div>
            <div :class="b('slot-resize-handle')" @mousedown.stop="startResize(index, $event)" />
          </div>
        </div>
      </div>

      <!-- Right Panel: Slot Properties -->
      <div :class="b('properties')">
        <div :class="b('properties-header')">
          <h3>Properties</h3>
        </div>

        <div v-if="selectedSlot" :class="b('properties-content')">
          <a-form layout="vertical">
            <a-form-item label="Slot ID">
              <a-input v-model:value="selectedSlot.slotId" placeholder="slot-1" />
            </a-form-item>

            <a-form-item label="Position X (px)">
              <a-input-number
                v-model:value="selectedSlot.x"
                :min="0"
                :max="canvasWidth"
                style="width: 100%"
              />
            </a-form-item>

            <a-form-item label="Position Y (px)">
              <a-input-number
                v-model:value="selectedSlot.y"
                :min="0"
                :max="canvasHeight"
                style="width: 100%"
              />
            </a-form-item>

            <a-form-item label="Width (px)">
              <a-input-number
                v-model:value="selectedSlot.width"
                :min="50"
                :max="canvasWidth"
                style="width: 100%"
              />
            </a-form-item>

            <a-form-item label="Height (px)">
              <a-input-number
                v-model:value="selectedSlot.height"
                :min="50"
                :max="canvasHeight"
                style="width: 100%"
              />
            </a-form-item>

            <a-form-item label="Rotation (deg)">
              <a-slider
                v-model:value="selectedSlot.rotation"
                :min="-180"
                :max="180"
                :marks="{ 0: '0°', 90: '90°', '-90': '-90°' }"
              />
            </a-form-item>

            <a-form-item label="Z-Index">
              <a-input-number
                v-model:value="selectedSlot.zIndex"
                :min="0"
                :max="100"
                style="width: 100%"
              />
            </a-form-item>

            <a-button danger block @click="removeSlot(selectedSlotIndex)">
              Delete Slot
            </a-button>
          </a-form>
        </div>

        <div v-else :class="b('properties-empty')">
          <p>Select a slot to edit properties</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import bem from '@/utils/bem';

const b = bem('template-builder');

export default {
  name: 'TemplateBuilder',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      pages: [],
      currentPageIndex: 0,
      selectedSlotIndex: null,
      canvasWidth: 800,
      canvasHeight: 600,
      isDragging: false,
      isResizing: false,
      dragStartX: 0,
      dragStartY: 0,
      dragStartSlotX: 0,
      dragStartSlotY: 0,
      resizeStartWidth: 0,
      resizeStartHeight: 0,
    };
  },
  computed: {
    currentPage() {
      return this.pages[this.currentPageIndex] || {};
    },
    selectedSlot() {
      if (this.selectedSlotIndex !== null && this.currentPage.slots) {
        return this.currentPage.slots[this.selectedSlotIndex];
      }
      return null;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue && newValue.length > 0) {
          this.pages = JSON.parse(JSON.stringify(newValue));
        } else {
          this.pages = [this.createDefaultPage()];
        }
      },
    },
    pages: {
      deep: true,
      handler() {
        this.$emit('input', this.pages);
        this.$emit('update:value', this.pages);
      },
    },
  },
  mounted() {
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  },
  beforeUnmount() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    createDefaultPage() {
      return {
        pageNumber: 1,
        backgroundColor: '#ffffff',
        backgroundImageId: null,
        slots: [],
        textAreas: [],
      };
    },
    addPage() {
      const newPage = {
        ...this.createDefaultPage(),
        pageNumber: this.pages.length + 1,
      };
      this.pages.push(newPage);
      this.currentPageIndex = this.pages.length - 1;
    },
    removePage(index) {
      if (this.pages.length > 1) {
        this.pages.splice(index, 1);
        // Renumber pages
        this.pages = this.pages.map((page, i) => ({
          ...page,
          pageNumber: i + 1,
        }));
        if (this.currentPageIndex >= this.pages.length) {
          this.currentPageIndex = this.pages.length - 1;
        }
      }
    },
    addSlot() {
      const newSlot = {
        slotId: `slot-${Date.now()}`,
        orderImageId: null,
        imageUri: null,
        x: 100,
        y: 100,
        width: 200,
        height: 200,
        rotation: 0,
        zIndex: this.currentPage.slots.length,
        imagePosition: {
          x: 0,
          y: 0,
          zoom: 1,
          rotate: 0,
        },
        cropData: null,
      };
      this.currentPage.slots.push(newSlot);
      this.selectedSlotIndex = this.currentPage.slots.length - 1;
    },
    removeSlot(index) {
      this.currentPage.slots.splice(index, 1);
      this.selectedSlotIndex = null;
    },
    selectSlot(index) {
      this.selectedSlotIndex = index;
    },
    handleCanvasClick() {
      this.selectedSlotIndex = null;
    },
    getSlotStyle(slot) {
      return {
        position: 'absolute',
        left: `${slot.x}px`,
        top: `${slot.y}px`,
        width: `${slot.width}px`,
        height: `${slot.height}px`,
        transform: `rotate(${slot.rotation}deg)`,
        zIndex: slot.zIndex,
      };
    },
    startDrag(index, event) {
      this.isDragging = true;
      this.selectedSlotIndex = index;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartSlotX = this.selectedSlot.x;
      this.dragStartSlotY = this.selectedSlot.y;
    },
    startResize(index, event) {
      this.isResizing = true;
      this.selectedSlotIndex = index;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.resizeStartWidth = this.selectedSlot.width;
      this.resizeStartHeight = this.selectedSlot.height;
    },
    handleMouseMove(event) {
      if (this.isDragging && this.selectedSlot) {
        const deltaX = event.clientX - this.dragStartX;
        const deltaY = event.clientY - this.dragStartY;
        const maxX = this.canvasWidth - this.selectedSlot.width;
        const maxY = this.canvasHeight - this.selectedSlot.height;
        this.selectedSlot.x = Math.max(0, Math.min(maxX, this.dragStartSlotX + deltaX));
        this.selectedSlot.y = Math.max(0, Math.min(maxY, this.dragStartSlotY + deltaY));
      } else if (this.isResizing && this.selectedSlot) {
        const deltaX = event.clientX - this.dragStartX;
        const deltaY = event.clientY - this.dragStartY;
        this.selectedSlot.width = Math.max(50, this.resizeStartWidth + deltaX);
        this.selectedSlot.height = Math.max(50, this.resizeStartHeight + deltaY);
      }
    },
    handleMouseUp() {
      this.isDragging = false;
      this.isResizing = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables';

.template-builder {
  width: 100%;
  height: 100%;
  min-height: 600px;

  &__container {
    display: flex;
    height: 100%;
    gap: 16px;
  }

  &__sidebar {
    width: 200px;
    border: 1px solid $mercury-gray-color;
    border-radius: 4px;
    overflow: hidden;
  }

  &__sidebar-header {
    padding: 12px;
    border-bottom: 1px solid $mercury-gray-color;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      margin: 0;
      font-size: 14px;
    }
  }

  &__pages-list {
    max-height: 500px;
    overflow-y: auto;
  }

  &__page-item {
    padding: 12px;
    border-bottom: 1px solid $mercury-gray-color;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:hover {
      background-color: #f5f5f5;
    }

    &_active {
      background-color: #e6f7ff;
      border-left: 3px solid #1890ff;
    }
  }

  &__canvas-area {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &__canvas-toolbar {
    padding: 12px;
    border: 1px solid $mercury-gray-color;
    border-bottom: none;
    border-radius: 4px 4px 0 0;
    background-color: #fafafa;
  }

  &__canvas {
    position: relative;
    border: 1px solid $mercury-gray-color;
    border-radius: 0 0 4px 4px;
    background-color: #ffffff;
    background-image:
      linear-gradient(45deg, #f5f5f5 25%, transparent 25%),
      linear-gradient(-45deg, #f5f5f5 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #f5f5f5 75%),
      linear-gradient(-45deg, transparent 75%, #f5f5f5 75%);
    background-size: 20px 20px;
    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  }

  &__slot {
    border: 2px dashed #d9d9d9;
    background-color: rgba(255, 255, 255, 0.8);
    cursor: move;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border-color: #40a9ff;
    }

    &_selected {
      border-color: #1890ff;
      border-style: solid;
    }
  }

  &__slot-label {
    font-size: 12px;
    color: #8c8c8c;
    pointer-events: none;
  }

  &__slot-resize-handle {
    position: absolute;
    right: -5px;
    bottom: -5px;
    width: 10px;
    height: 10px;
    background-color: #1890ff;
    cursor: nwse-resize;
    border-radius: 2px;
  }

  &__properties {
    width: 280px;
    border: 1px solid $mercury-gray-color;
    border-radius: 4px;
    overflow: hidden;
  }

  &__properties-header {
    padding: 12px;
    border-bottom: 1px solid $mercury-gray-color;
    background-color: #fafafa;

    h3 {
      margin: 0;
      font-size: 14px;
    }
  }

  &__properties-content {
    padding: 16px;
    max-height: 600px;
    overflow-y: auto;
  }

  &__properties-empty {
    padding: 24px;
    text-align: center;
    color: #8c8c8c;
  }
}
</style>
